import api from "./api";
import { isOfflineMode } from "../utils/networkUtils";

const TICKET_CACHE_KEY = "cached_ticket_data_";

/**
 * Update ticket data - only works online
 * @param {Object} params - The update params
 * @param {string} params.userId - The user ID
 * @param {string} params.text - The ticket text
 * @param {string} params.fontFamily - The font family
 * @param {number} params.fontSize - The font size
 * @param {string} params.fontWeight - The font weight
 * @returns {Promise<Object>} The updated ticket data
 */
export const updateTicketData = async ({ userId, text, fontFamily, fontSize, fontWeight }) => {
    if (isOfflineMode()) {
        console.warn("Cannot update ticket data in offline mode");
        throw new Error("Cannot update ticket data in offline mode");
    }
    
    const response = await api.put(`/ticket/${userId}`, { text, fontFamily, fontSize, fontWeight });
    
    // Update the cache after a successful update
    if (response && response.data) {
        localStorage.setItem(TICKET_CACHE_KEY + userId, JSON.stringify(response.data));
    }
    
    return response.data;
};

/**
 * Get ticket data - works online and offline
 * @param {string} userId - The user ID
 * @returns {Promise<Object>} The ticket data
 */
export const getTicketData = async (userId) => {
    try {
        // If we're offline, use cached data
        if (isOfflineMode()) {
            return getTicketFromCache(userId);
        }
        
        // If online, get fresh data and update cache
        const response = await api.get(`/ticket/${userId}`);
        
        if (response && response.data) {
            // Store in cache for offline use
            localStorage.setItem(TICKET_CACHE_KEY + userId, JSON.stringify(response.data));
            return response.data;
        }
        
        return null;
    } catch (error) {
        console.error("[Ticket] Error fetching ticket data:", error);
        
        // If API call fails, try to get from cache as fallback
        return getTicketFromCache(userId);
    }
};

/**
 * Get ticket data from localStorage cache
 * @param {string} userId - The user ID
 * @returns {Object|null} The cached ticket data or null if not found
 */
const getTicketFromCache = (userId) => {
    try {
        const cachedData = localStorage.getItem(TICKET_CACHE_KEY + userId);
        
        if (cachedData) {
            return JSON.parse(cachedData);
        }
        
        return null;
    } catch (error) {
        console.error("[Ticket] Error getting ticket from cache:", error);
        return null;
    }
};

/**
 * Clear ticket cache data
 * @param {string} userId - The user ID (optional, if not provided clears all ticket caches)
 */
export const clearTicketCache = (userId) => {
    if (userId) {
        localStorage.removeItem(TICKET_CACHE_KEY + userId);
    } else {
        // Clear all ticket caches
        Object.keys(localStorage)
            .filter(key => key.startsWith(TICKET_CACHE_KEY))
            .forEach(key => localStorage.removeItem(key));
    }
};
